import { useReactFlow } from '@xyflow/react';
import { useAtomValue } from 'jotai';
import { memo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useParams } from 'react-router-dom';

import { toast } from '@/lib/v2/components';
import { CommonFlowCard, CommonFlowCardProps } from '@/lib/v3/components';

import { atomJourneyDetail } from '@/modules/AutomationModuleNew/atoms/flows';
import { AUTOMATION_PATHS, JOURNEY_BACK_URL_KEY } from '@/modules/AutomationModuleNew/constants';
import { useUpdateJourney } from '@/modules/AutomationModuleNew/hooks/Flows';
import { useSendEmail } from '@/modules/AutomationModuleNew/hooks/Flows/useSendEmail';
import { useNavigateAndBack } from '@/modules/AutomationModuleNew/hooks/useNavigateAndBack';
import { SendEmailData } from '@/modules/AutomationModuleNew/types/flowsTypes';

interface SendEmailCardProps extends CommonFlowCardProps {
  data: SendEmailData;
}

const SendEmailCard = (props: SendEmailCardProps) => {
  const { data, editAction, id: nodeId } = props;
  const { actionId } = data.input;
  const { journeyId } = useParams();
  const journeyDetail = useAtomValue(atomJourneyDetail);
  const info = actionId.value ? `Action Id: ${actionId.value.toString()}` : undefined;
  const { getCampaignByActionId, handleCreateRule, isLoading } = useSendEmail();
  const { updateJourney } = useUpdateJourney();
  const { updateNodeData } = useReactFlow();
  const { navigateAndSavePathname } = useNavigateAndBack(JOURNEY_BACK_URL_KEY);

  const handleGoToSendEmail = useCallback(async () => {
    const { actionId } = data.input;
    if (actionId.value) {
      await getCampaignByActionId(actionId.value);
    } else {
      const response = await handleCreateRule(
        `${journeyDetail?.name ?? 'Send Email '} ${Date.now()}`
      );

      if (!response?.isValidInput) {
        toast({
          id: 'sendEmailCreated',
          title: 'Error',
          body: response?.message,
          variant: 'error',
        });
        return;
      }

      const { id: actionId, campaignId } = response.data;

      flushSync(() => {
        updateNodeData(nodeId || '', {
          input: {
            ...data.input,
            actionId: {
              value: actionId,
              type: 'literal',
            },
          },
        });
      });

      const updatedResponse = await updateJourney(Number(journeyId));
      if (updatedResponse?.success) {
        navigateAndSavePathname(
          `/v2/${AUTOMATION_PATHS.MODULE_PATH}/${AUTOMATION_PATHS.JOURNEYS_PATH}/${campaignId}/action/${actionId}/email-trigger/email`
        );
      }
    }
  }, [
    data.input,
    getCampaignByActionId,
    handleCreateRule,
    journeyDetail?.name,
    journeyId,
    navigateAndSavePathname,
    nodeId,
    updateJourney,
    updateNodeData,
  ]);

  return (
    <CommonFlowCard
      {...props}
      editAction={{ ...editAction, onClick: handleGoToSendEmail }}
      info={info}
      isLoading={isLoading}
    />
  );
};

export default memo(SendEmailCard);
